import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import OperacoesSelector from './OperacoesSelector';
import {
  Save, FileText, Package, Layers, Box, Wrench, Clock,
  Sparkles, ChevronDown, ChevronUp, Zap,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INIT_FIN = { valor_hora: 10.0, operadores_simultaneos: 1, horas_trabalho_dia: 7.67 };

export default function ModalEditarOF({
  of,              // OF being edited
  open,
  onOpenChange,
  initialStep = 1,
  // Data
  operacoes = [],
  allCategorias = [],
  categoriasEspeciais = [],
  operadores = [],
  modelos = [],
  clientesDB = [],
  allTags = [],
  // Shared
  loading = false,
  setLoading,
  isAdmin = false,
  // Callbacks
  onSuccess,                    // () => reload OFs
  filtrarOperadoresPorPermissao = null,
  existeOperadorComPermissao = false,
  onCriarMinuta = null,         // (ops) => open minuta modal
  onOpenResgate = null,         // (ofId) => open resgate modal
}) {
  const [form, setForm] = useState({});
  const [step, setStep] = useState(1);
  const [lotesInt, setLotesInt] = useState([]);
  const [opsInternas, setOpsInternas] = useState([]);
  const opsInternasRef = useRef([]);
  const [notificarOps, setNotificarOps] = useState(true);
  const [opsAdicionadas, setOpsAdicionadas] = useState([]);
  const [finConf, setFinConf] = useState({ ...INIT_FIN });

  // Initialize from `of` when dialog opens
  useEffect(() => {
    if (open && of) {
      setForm({
        numero_of: of.numero_of || '',
        artigo: of.artigo || '',
        quantidade_total: of.quantidade_total?.toString() || '',
        cliente: of.cliente || '',
        previsao_entrada: of.previsao_entrada || '',
        data_entrega: of.data_entrega || '',
        observacoes: of.observacoes || '',
        urgente: of.urgente || false,
        aguardar_items: of.aguardar_items || [],
        categorias_especiais: of.categorias_especiais || [],
      });
      setLotesInt(
        of.lotes?.map((l) => ({ numero_lote: l.numero_lote, quantidade: l.quantidade?.toString() })) ||
          [{ numero_lote: 1, quantidade: of.quantidade_total?.toString() }]
      );
      const opsOrdenadas = (of.operacoes || [])
        .map((op, idx) => ({ ...op, id_temp: `edit-${idx}` }))
        .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
      setOpsInternas(opsOrdenadas);
      opsInternasRef.current = opsOrdenadas;
      setStep(initialStep);
      setOpsAdicionadas([]);
      setNotificarOps(true);
      if (of.financeiro) {
        setFinConf({ ...INIT_FIN, ...of.financeiro });
      } else {
        setFinConf({ ...INIT_FIN });
      }
    }
  }, [open, of, initialStep]);

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Track changes in form fields
  const handleFormChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  // Reset dirty flag when dialog opens
  useEffect(() => {
    if (open) setIsDirty(false);
  }, [open]);

  const handleClose = (v) => {
    if (!v) {
      if (isDirty) {
        setShowCloseConfirm(true);
      } else {
        onOpenChange(false);
      }
    } else {
      onOpenChange(v);
    }
  };

  const confirmClose = () => {
    setShowCloseConfirm(false);
    setIsDirty(false);
    onOpenChange(false);
  };

  const getOpInfo = (id) => operacoes.find((op) => op.id === id);

  const handleAplicarModelo = (modelo) => {
    if (
      opsInternas.length > 0 &&
      !window.confirm(`Já existem ${opsInternas.length} operações. Substituir com "${modelo.nome}"?`)
    )
      return;
    setOpsInternas(
      modelo.operacoes.map((op, i) => ({ ...op, ordem: i, id_temp: `edit-m-${Date.now()}-${i}` }))
    );
    setIsDirty(true);
    toast.success(`Modelo "${modelo.nome}" aplicado`);
  };

  const calcularPrevisoes = () => {
    const quantidade = parseInt(form.quantidade_total) || 0;
    const horasDia = finConf.horas_trabalho_dia || 7.67;
    const operadoresSimul = finConf.operadores_simultaneos || 1;
    const valorHora = finConf.valor_hora || 10;

    const opsCom = opsInternas.map((op) => {
      const tempo = op.tempo_ajustado || 0;
      const tempoTotal = tempo * quantidade;
      const tempoProducao = tempoTotal / operadoresSimul;
      const tempoProducaoHoras = tempoProducao / 3600;
      const custo = tempoProducaoHoras * valorHora * operadoresSimul;
      return {
        ...op, tempoUnitario: tempo, tempoTotalSegundos: tempoTotal,
        tempoProducaoSegundos: tempoProducao, tempoProducaoHoras: tempoProducaoHoras,
        custoOperacao: custo, tempoDias: tempoProducaoHoras / horasDia,
      };
    });

    const tempoTotalSeg = opsCom.reduce((a, op) => a + op.tempoTotalSegundos, 0);
    const tempoProducaoTotal = tempoTotalSeg / operadoresSimul;
    const tempoProducaoHoras = tempoProducaoTotal / 3600;
    const custoTotal = tempoProducaoHoras * valorHora * operadoresSimul;

    return {
      operacoes: opsCom,
      totais: {
        tempoTotalSegundos: tempoTotalSeg, tempoProducaoTotal, tempoProducaoHoras,
        tempoTotalDias: tempoProducaoHoras / horasDia,
        custoTotal, custoPorPeca: quantidade > 0 ? custoTotal / quantidade : 0,
        quantidade, operadoresSimultaneos: operadoresSimul,
      },
    };
  };

  // ── Recalcular registos antigos quando o tempo de uma operação muda ──
  // Cada registo guarda o tempo em vigor quando foi iniciado. Se o gestor mudar o
  // tempo de uma operação que já tem registos, perguntamos se quer aplicar o novo
  // tempo também aos registos já feitos (para os relatórios mudarem logo).
  const [recalcPrompt, setRecalcPrompt] = useState(null); // { itens: [...], total }
  const [verificandoTempos, setVerificandoTempos] = useState(false);

  const handleSave = async () => {
    if (!of) return;
    const originais = {};
    (of.operacoes || []).forEach((o) => { originais[o.operacao_id] = Number(o.tempo_ajustado || 0); });
    const alterados = opsInternas.filter((op) => (
      originais[op.operacao_id] !== undefined
      && Number(op.tempo_ajustado || 0) > 0
      && Number(op.tempo_ajustado || 0) !== originais[op.operacao_id]
    ));
    if (alterados.length === 0) { executarGuardar(null); return; }
    setVerificandoTempos(true);
    try {
      const res = await axios.get(`${API}/ofs/${of.id}/registos-tempos`, {
        params: { operacao_ids: alterados.map((o) => o.operacao_id).join(',') },
      });
      const info = res.data?.operacoes || {};
      const itens = alterados
        .map((op) => {
          const g = info[op.operacao_id];
          const novo = Number(op.tempo_ajustado);
          if (!g || !g.total || (g.tempos.length === 1 && Number(g.tempos[0]) === novo)) return null;
          const opInfo = getOpInfo(op.operacao_id);
          return {
            operacao_id: op.operacao_id,
            codigo: op.operacao_codigo || opInfo?.codigo || '',
            nome: op.operacao_nome || opInfo?.nome || '',
            tempo_antigo: originais[op.operacao_id],
            tempo_novo: novo,
            registos: g.total,
            ativos: g.ativos,
          };
        })
        .filter(Boolean);
      if (itens.length === 0) { executarGuardar(null); return; }
      setRecalcPrompt({ itens, total: itens.reduce((a, i) => a + i.registos, 0) });
    } catch (e) {
      // Se não for possível verificar, guardar normalmente (comportamento anterior)
      executarGuardar(null);
    } finally {
      setVerificandoTempos(false);
    }
  };

  const executarGuardar = async (recalcItens) => {
    if (!of) return;
    setLoading && setLoading(true);
    try {
      const opsFormatadas = opsInternas.map((op) => ({
        operacao_id: op.operacao_id,
        tempo_ajustado: op.tempo_ajustado,
        operadores_atribuidos: op.operadores_atribuidos || [],
        ordem: op.ordem,
        grupo: op.grupo,
      }));

      const hasApprovedLotes = of.lotes?.some(
        (l) => ['aprovado', 'em_curso', 'concluido'].includes(l.status)
      );

      const updateData = {
        numero_of: form.numero_of, artigo: form.artigo,
        quantidade_total: parseInt(form.quantidade_total),
        cliente: form.cliente,
        previsao_entrada: form.previsao_entrada || undefined,
        data_entrega: form.data_entrega || undefined,
        observacoes: form.observacoes || undefined,
        urgente: form.urgente || false,
        aguardar_items: form.aguardar_items || [],
        categorias_especiais: form.categorias_especiais || [],
        operacoes: opsFormatadas,
        financeiro: finConf,
      };

      if (!hasApprovedLotes) {
        updateData.lotes = lotesInt.map((l) => ({
          numero_lote: l.numero_lote, quantidade: parseInt(l.quantidade) || 0,
        }));
      }

      await axios.put(`${API}/ofs/${of.id}`, updateData);

      // Notificar operadores adicionados recentemente
      if (notificarOps && opsAdicionadas.length > 0 && of.status === 'em_curso') {
        for (const { operadorId, operacaoId } of opsAdicionadas) {
          const oper = operadores.find((o) => o.id === operadorId);
          const operacao = operacoes.find((o) => o.id === operacaoId);
          if (oper && operacao) {
            try {
              const loteAtivo = of.lotes?.find((l) => l.status === 'aprovado') || of.lotes?.[0];
              await axios.post(`${API}/alertas`, {
                titulo: 'Nova Tarefa Atribuída',
                mensagem: `Foi adicionado à operação "${operacao.codigo || ''} - ${operacao.nome}" na OF ${of.numero_of}.`,
                prioridade: 'normal', destinatarios: 'todos',
                data_notificacao: new Date().toISOString(),
                destinatario_especifico: operadorId,
                dados_of: {
                  of_id: of.id, of_numero: of.numero_of,
                  lote_id: loteAtivo?.id || '', lote_codigo: loteAtivo?.codigo_lote || '',
                  operacao_id: operacaoId, operacao_nome: operacao.nome,
                },
              });
            } catch (e) { console.warn('Erro ao enviar alerta:', e); }
          }
        }
        toast.success(`Alertas enviados para ${opsAdicionadas.length} operador(es)`);
      }

      if (recalcItens && recalcItens.length) {
        try {
          const rr = await axios.post(`${API}/ofs/${of.id}/recalcular-tempos`, {
            operacoes: recalcItens.map((i) => ({ operacao_id: i.operacao_id, tempo: i.tempo_novo })),
          });
          toast.success(`${rr.data?.total_registos_atualizados ?? 0} registo(s) atualizados com o novo tempo — relatórios já recalculados`);
        } catch (e) {
          toast.error(e.response?.data?.detail || 'A OF foi guardada, mas falhou a atualização dos registos antigos');
        }
      }

      toast.success('OF atualizada com sucesso!');
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (e) {
      toast.error('Erro ao atualizar OF');
      console.error(e);
    } finally {
      setLoading && setLoading(false);
    }
  };

  if (!of) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className={
          step === 3
            ? 'max-w-[98vw] w-[98vw] h-[95vh] flex flex-col overflow-hidden'
            : 'max-w-5xl max-h-[90vh] overflow-y-auto'
        }
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-heading">Editar OF — {of.numero_of}</DialogTitle>
          <div className="flex gap-2 mt-2">
            {[
              { s: 1, label: 'Dados' },
              { s: 3, label: `Operações (${opsInternas.length})` },
            ].map(({ s, label }) => (
              <button
                key={s}
                onClick={() => setStep(s)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  step === s ? 'bg-primary text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {label}
              </button>
            ))}
            {isAdmin && (
              <button
                onClick={() => setStep(4)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${step === 4 ? 'bg-sage text-white' : 'bg-sage/10 text-sage hover:bg-sage/20'}`}
              >
                💰 Financeiro
              </button>
            )}
          </div>
        </DialogHeader>

        {/* Tab Dados */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Número OF *</label>
                <Input value={form.numero_of || ''} onChange={(e) => { setForm({ ...form, numero_of: e.target.value }); setIsDirty(true); }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Quantidade Total *</label>
                <Input type="number" value={form.quantidade_total || ''} onChange={(e) => { setForm({ ...form, quantidade_total: e.target.value }); setIsDirty(true); }} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Modelo *</label>
                <Input value={form.artigo || ''} onChange={(e) => { setForm({ ...form, artigo: e.target.value }); setIsDirty(true); }} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Marca *</label>
                <select value={form.cliente || ''} onChange={(e) => { setForm({ ...form, cliente: e.target.value }); setIsDirty(true); }}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary bg-white">
                  <option value="">Selecione uma marca</option>
                  {clientesDB.map((c) => (
                    <option key={c.id} value={c.nome}>
                      {c.codigo ? `${c.nome} (${c.codigo})` : c.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Previsão de Entrada</label>
                <Input type="date" value={form.previsao_entrada || ''} onChange={(e) => { setForm({ ...form, previsao_entrada: e.target.value }); setIsDirty(true); }} data-testid="edit-previsao-entrada-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Data Entrega</label>
                <Input type="date" value={form.data_entrega || ''} onChange={(e) => { setForm({ ...form, data_entrega: e.target.value }); setIsDirty(true); }} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Observações</label>
              <textarea value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} className="w-full px-3 py-2 border border-stone-300 rounded-lg" rows={2} />
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border border-stone-200 bg-stone-50">
              <input type="checkbox" id="urgente-edit" checked={form.urgente || false} onChange={(e) => { setForm({ ...form, urgente: e.target.checked }); setIsDirty(true); }} className="h-5 w-5 rounded border-stone-300 text-red-600" />
              <label htmlFor="urgente-edit" className="flex items-center gap-2 cursor-pointer">
                <Zap className="h-5 w-5 text-red-500" />
                <span className="font-medium text-stone-700">Marcar como Urgente</span>
              </label>
            </div>
            {/* Categorias Especiais */}
            {categoriasEspeciais.length > 0 && (
              <div className="p-4 rounded-lg border border-purple-200 bg-purple-50">
                <p className="font-medium text-stone-700 mb-2 flex items-center gap-2"><Sparkles className="h-5 w-5 text-purple-600" />Categorias Especiais</p>
                <div className="space-y-2">
                  {categoriasEspeciais.map((cat) => {
                    const current = form.categorias_especiais || [];
                    const isSel = current.includes(cat.id);
                    return (
                      <div key={cat.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${isSel ? 'bg-purple-100 border-2 border-purple-400' : 'bg-white border border-stone-200'}`}
                        onClick={() => { setForm((prev) => ({ ...prev, categorias_especiais: isSel ? current.filter((id) => id !== cat.id) : [...current, cat.id] })); setIsDirty(true); }}
                      >
                        <input type="checkbox" checked={isSel} onChange={() => {}} className="h-4 w-4 rounded pointer-events-none" />
                        <span className="font-medium text-stone-800">{cat.nome}</span>
                        {cat.codigo && <span className="text-xs font-mono px-1.5 py-0.5 bg-purple-200 text-purple-700 rounded">{cat.codigo}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Itens Aguardar */}
            <div className="p-4 rounded-lg border border-amber-200 bg-amber-50">
              <p className="font-medium text-stone-700 mb-3 flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" />Itens em falta (A Aguardar)</p>
              <div className="space-y-3">
                {[{ tipo: 'estrutura', label: 'Estrutura', Icon: Layers }, { tipo: 'pecas', label: 'Peças', Icon: Box }, { tipo: 'acessorios', label: 'Acessórios', Icon: Wrench }].map(({ tipo, label, Icon }) => {
                  const item = form.aguardar_items?.find((i) => i.tipo === tipo) || null;
                  const isChecked = !!item;
                  return (
                    <div key={tipo} className="flex items-center gap-3">
                      <input type="checkbox" id={`aguardar-edit-${tipo}`} checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setForm({ ...form, aguardar_items: [...(form.aguardar_items || []), { tipo, recebido: false, data_prevista: '' }] }); setIsDirty(true);
                          } else {
                            setForm({ ...form, aguardar_items: (form.aguardar_items || []).filter((i) => i.tipo !== tipo) }); setIsDirty(true);
                          }
                        }}
                        className="h-5 w-5 rounded border-stone-300"
                      />
                      <label htmlFor={`aguardar-edit-${tipo}`} className="flex items-center gap-2 cursor-pointer flex-1">
                        <Icon className="h-5 w-5 text-amber-600" />
                        <span className="font-medium text-stone-700">{label}</span>
                      </label>
                      {isChecked && (
                        <Input type="date" value={item?.data_prevista || ''} onChange={(e) => { setForm({ ...form, aguardar_items: (form.aguardar_items || []).map((i) => i.tipo === tipo ? { ...i, data_prevista: e.target.value } : i) }); setIsDirty(true); }} className="w-40 h-8 text-sm" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Tab Operações */}
        {step === 3 && (
          <div className="flex-1 flex flex-col overflow-hidden space-y-4 mt-2">
            {/* Carregar Minuta */}
            <div className="flex items-center gap-4 flex-shrink-0">
              <label className="text-sm font-medium text-stone-700 whitespace-nowrap">Carregar Minuta:</label>
              {modelos.length > 0 ? (
                <select
                  value=""
                  onChange={(e) => { if (e.target.value) { const m = modelos.find((m) => m.id === e.target.value); if (m) handleAplicarModelo(m); e.target.value = ''; } }}
                  className="flex-1 px-3 py-2 border border-stone-300 rounded-lg bg-white text-sm"
                  data-testid="edit-modelo-selector"
                >
                  <option value="">Selecione uma minuta...</option>
                  {modelos.map((m) => (
                    <option key={m.id} value={m.id}>
                      {`${m.nome} (${m.operacoes?.length || 0} operações)`}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-sm text-stone-500 italic">Nenhuma minuta disponível</span>
              )}
            </div>

            <OperacoesSelector
              operacoes={operacoes}
              selectedOperacoes={opsInternas}
              setSelectedOperacoes={(opsOrFn) => {
                const prev = opsInternasRef.current;
                const ops = typeof opsOrFn === 'function' ? opsOrFn(prev) : opsOrFn;
                if (!Array.isArray(ops)) return;
                // Track newly added operators for notifications
                const prevIds = new Set(prev.flatMap((op) => op.operadores_atribuidos || []));
                const novosOps = [];
                ops.forEach((op) => {
                  (op.operadores_atribuidos || []).forEach((opId) => {
                    if (!prevIds.has(opId)) {
                      novosOps.push({ operadorId: opId, operacaoId: op.operacao_id });
                    }
                  });
                });
                if (novosOps.length > 0) setOpsAdicionadas((p) => [...p, ...novosOps]);
                setOpsInternas(ops);
                opsInternasRef.current = ops;
                setIsDirty(true);
              }}
              categorias={allCategorias}
              operadores={operadores}
              tags={allTags}
              modelos={modelos}
              onAplicarModelo={handleAplicarModelo}
              filtrarOperadoresPorPermissao={filtrarOperadoresPorPermissao}
              getOperacaoInfo={getOpInfo}
            />

            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1">
                  {['A', 'B', 'C', 'D'].map((g, i) => (
                    <div key={g} className="flex items-center gap-1">
                      <div className={`w-3 h-3 rounded ${['bg-blue-500', 'bg-green-500', 'bg-amber-500', 'bg-purple-500'][i]}`} />
                      <span className="text-xs text-stone-500">Grupagem {g}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-between gap-3 pt-2 flex-shrink-0">
                <Button variant="outline" onClick={() => onCriarMinuta && onCriarMinuta(opsInternas)} disabled={opsInternas.length === 0} className="text-purple-600 border-purple-300 hover:bg-purple-50">
                  <FileText className="mr-2 h-4 w-4" />Criar Minuta
                </Button>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
                  <Button onClick={handleSave} disabled={loading || verificandoTempos} data-testid="btn-guardar-of">
                    <Save className="mr-2 h-4 w-4" />{loading ? 'A guardar...' : 'Guardar Alterações'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Financeiro - Admin */}
        {step === 4 && isAdmin && (() => {
          const previsoes = calcularPrevisoes();
          return (
            <div className="space-y-6">
              <Card className="p-4 border-sage/30 bg-sage/5">
                <h3 className="font-semibold text-sage-dark mb-4">⚙️ Parâmetros de Cálculo</h3>
                <div className="grid grid-cols-3 gap-4">
                  {[
                    { label: 'Valor/Hora (€)', key: 'valor_hora', type: 'float', step: 0.01, min: 0 },
                    { label: 'Operadores Simultâneos', key: 'operadores_simultaneos', type: 'int', min: 1, max: 20 },
                    { label: 'Horas/Dia de Trabalho', key: 'horas_trabalho_dia', type: 'float', step: 0.5, min: 0.5, max: 24 },
                  ].map(({ label, key, type, step: s, min, max }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-stone-700 mb-2">{label}</label>
                      <Input type="number" step={s} min={min} max={max} value={finConf[key]}
                        onChange={(e) => { setFinConf({ ...finConf, [key]: type === 'int' ? parseInt(e.target.value) || 1 : parseFloat(e.target.value) || 0 }); setIsDirty(true); }}
                        className="font-bold text-lg"
                      />
                    </div>
                  ))}
                </div>
              </Card>

              <div className="grid grid-cols-4 gap-4">
                {[
                  { label: 'Quantidade', val: previsoes.totais.quantidade.toLocaleString(), unit: 'peças', colors: 'from-primary/10 to-primary/5 text-primary' },
                  { label: 'Tempo Produção', val: (previsoes.totais.tempoProducaoHoras * 60).toFixed(0), unit: 'min', colors: 'from-sage/10 to-sage/5 text-sage' },
                  { label: 'Dias', val: previsoes.totais.tempoTotalDias.toFixed(2), unit: `(${finConf.horas_trabalho_dia}h/dia)`, colors: 'from-sand/20 to-sand/10 text-stone-700' },
                  { label: 'Custo Total', val: `€${previsoes.totais.custoTotal.toFixed(2)}`, unit: `(€${previsoes.totais.custoPorPeca.toFixed(2)}/peça)`, colors: 'from-terracotta/10 to-terracotta/5 text-terracotta' },
                ].map(({ label, val, unit, colors }) => (
                  <Card key={label} className={`p-4 text-center bg-gradient-to-br ${colors.split(' ')[0]} ${colors.split(' ')[1]}`}>
                    <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">{label}</p>
                    <p className={`text-3xl font-bold ${colors.split(' ')[2]}`}>{val}</p>
                    <p className="text-sm text-stone-600">{unit}</p>
                  </Card>
                ))}
              </div>

              {opsInternas.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-stone-100">
                      <tr>
                        {['#', 'Operação', 'Tempo/Peça', 'Minutos', 'T.Produção', 'Dias', 'Custo'].map((h) => (
                          <th key={h} className={`py-3 px-4 text-sm font-semibold text-stone-600 ${h === '#' || h === 'Operação' ? 'text-left' : 'text-right'}`}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previsoes.operacoes.map((op, idx) => {
                        const opInfo = getOpInfo(op.operacao_id);
                        return (
                          <tr key={op.id_temp || op.operacao_id} className="border-b border-stone-100 hover:bg-stone-50">
                            <td className="py-3 px-4"><span className="w-6 h-6 rounded-full bg-primary text-white text-xs flex items-center justify-center font-bold">{idx + 1}</span></td>
                            <td className="py-3 px-4"><p className="font-medium">{opInfo?.nome || 'N/A'}</p><p className="text-xs text-stone-500 font-mono">{opInfo?.codigo || ''}</p></td>
                            <td className="py-3 px-4 text-right font-mono text-sm">{op.tempoUnitario}s</td>
                            <td className="py-3 px-4 text-right font-mono text-sm">{(op.tempoTotalSegundos / 60).toFixed(0)}min</td>
                            <td className="py-3 px-4 text-right font-bold text-sage">{(op.tempoProducaoHoras * 60).toFixed(0)}min</td>
                            <td className="py-3 px-4 text-right text-stone-600">{op.tempoDias.toFixed(2)}</td>
                            <td className="py-3 px-4 text-right font-bold text-terracotta">€{op.custoOperacao.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Botão Exportar PDF Financeiro */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={async () => {
                  try {
                    // Guardar antes de gerar o PDF
                    const opsFormatadas = opsInternas.map((op, idx) => ({
                      operacao_id: op.operacao_id,
                      operacao_codigo: op.operacao_codigo || getOpInfo(op.operacao_id)?.codigo || '',
                      operacao_nome: op.operacao_nome || getOpInfo(op.operacao_id)?.nome || '',
                      tempo_ajustado: op.tempo_ajustado || 0,
                      ordem: op.ordem ?? idx,
                      operadores_atribuidos: op.operadores_atribuidos || [],
                    }));
                    await axios.put(API + '/ofs/' + of.id, {
                      numero_of: form.numero_of, artigo: form.artigo,
                      quantidade_total: parseInt(form.quantidade_total),
                      cliente: form.cliente,
                      previsao_entrada: form.previsao_entrada || undefined,
                      data_entrega: form.data_entrega || undefined,
                      observacoes: form.observacoes || undefined,
                      urgente: form.urgente || false,
                      aguardar_items: form.aguardar_items || [],
                      categorias_especiais: form.categorias_especiais || [],
                      operacoes: opsFormatadas,
                      financeiro: finConf,
                    });
                  } catch(e) { /* continue even if save fails */ }

                  // Gerar PDF real via backend
                  try {
                    const previsoes = calcularPrevisoes();
                    const payload = {
                      numero_of: form.numero_of,
                      cliente: form.cliente || '',
                      artigo: form.artigo || '',
                      quantidade_total: parseInt(form.quantidade_total) || 0,
                      valor_hora: finConf.valor_hora,
                      operadores_simultaneos: finConf.operadores_simultaneos,
                      horas_trabalho_dia: finConf.horas_trabalho_dia,
                      tempo_producao_horas: previsoes.totais.tempoProducaoHoras,
                      tempo_total_dias: previsoes.totais.tempoTotalDias,
                      custo_total: previsoes.totais.custoTotal,
                      custo_por_peca: previsoes.totais.custoPorPeca,
                      operacoes: previsoes.operacoes.map(op => {
                        const info = getOpInfo(op.operacao_id);
                        return {
                          nome: info?.nome || 'N/A',
                          codigo: info?.codigo || '',
                          tempo_unitario: op.tempoUnitario,
                          tempo_total_seg: op.tempoTotalSegundos,
                          tempo_producao_horas: op.tempoProducaoHoras,
                          tempo_dias: op.tempoDias,
                          custo: op.custoOperacao,
                        };
                      }),
                    };
                    const res = await axios.post(API + '/ofs/financeiro-pdf', payload, { responseType: 'blob' });
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'Financeiro_OF_' + form.numero_of + '.pdf';
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    toast.success('PDF exportado com sucesso');
                  } catch (err) {
                    console.error('Erro ao gerar PDF:', err);
                    toast.error('Erro ao gerar PDF');
                  }
                }} className="bg-red-700 hover:bg-red-800 text-white" data-testid="btn-export-pdf-financeiro">
                  <FileText className="mr-2 h-4 w-4" /> Exportar PDF
                </Button>
              </div>
            </div>
          );
        })()}

        {/* Notificação Operadores */}
        {of.status === 'em_curso' && opsAdicionadas.length > 0 && (
          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200 mt-4">
            <input type="checkbox" id="notificar-ops-edit" checked={notificarOps} onChange={(e) => setNotificarOps(e.target.checked)} className="h-4 w-4 rounded border-stone-300" />
            <label htmlFor="notificar-ops-edit" className="text-sm text-blue-700 flex-1">
              <span className="font-medium">Notificar {opsAdicionadas.length} operador(es)</span>
              <span className="block text-xs text-blue-600">Enviar alerta sobre a nova tarefa atribuída</span>
            </label>
          </div>
        )}

        {/* Footer buttons (not shown in ops step - it has own buttons) */}
        {step !== 3 && (
          <div className="flex justify-end gap-3 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={loading || verificandoTempos} data-testid="btn-guardar-of">
              <Save className="mr-2 h-4 w-4" />{loading ? 'A guardar...' : 'Guardar Alterações'}
            </Button>
          </div>
        )}
      </DialogContent>

      {/* Confirmação ao fechar sem guardar */}
      {/* Pergunta: aplicar o novo tempo também aos registos já feitos? */}
      <AlertDialog open={!!recalcPrompt} onOpenChange={(v) => { if (!v) setRecalcPrompt(null); }}>
        <AlertDialogContent className="max-w-lg" data-testid="recalc-tempos-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Atualizar também os registos já feitos?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-stone-600">
                <p>Alterou o tempo de operações que já têm registos nesta OF. Esses registos foram feitos com o tempo antigo.</p>
                <div className="border rounded-lg divide-y max-h-56 overflow-y-auto">
                  {recalcPrompt?.itens.map((i) => (
                    <div key={i.operacao_id} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`recalc-item-${i.codigo}`}>
                      <span className="min-w-0 truncate"><strong className="text-blue-700 font-mono">{i.codigo}</strong> {i.nome}</span>
                      <span className="shrink-0 text-stone-700 tabular-nums">
                        <span className="line-through text-stone-400">{i.tempo_antigo}s</span> → <strong>{i.tempo_novo}s</strong>
                        <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{i.registos} registo{i.registos !== 1 ? 's' : ''}</span>
                      </span>
                    </div>
                  ))}
                </div>
                <ul className="text-xs space-y-1 list-disc pl-4">
                  <li><strong>Só novos registos:</strong> os registos já feitos mantêm o tempo antigo (os relatórios passados não mudam).</li>
                  <li><strong>Atualizar também os existentes:</strong> os {recalcPrompt?.total} registos passam a usar o novo tempo e a eficiência é recalculada — relatórios, rankings e Ranking Final mudam logo.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="recalc-cancelar-btn">Voltar</AlertDialogCancel>
            <Button variant="outline" onClick={() => { setRecalcPrompt(null); executarGuardar(null); }} data-testid="recalc-so-novos-btn">
              Só novos registos
            </Button>
            <Button className="bg-amber-600 hover:bg-amber-700" onClick={() => { const itens = recalcPrompt.itens; setRecalcPrompt(null); executarGuardar(itens); }} data-testid="recalc-atualizar-btn">
              Atualizar também os {recalcPrompt?.total}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar sem guardar?</AlertDialogTitle>
            <AlertDialogDescription>
              As alterações feitas não foram guardadas. Tem a certeza que quer fechar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="btn-cancelar-fechar">Voltar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmClose}
              className="bg-red-600 hover:bg-red-700"
              data-testid="btn-confirmar-fechar"
            >
              Fechar sem guardar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
